import { ChevronUp, PackageSearch } from "lucide-react";
import type { Ref } from "react";

import { UpgradeButton } from "@/components/appshell/billing/UpgradeButton";
import { AccountChip } from "@/components/shell/AccountChip";
import { CommandPalette, type AtlasSearchHandle } from "@/components/shell/CommandPalette";
import { Button } from "@/components/ui/button";
import { Tip } from "@/components/ui/overlays";
import { EngineStatus } from "@/components/feedback";
import { useEngine } from "@/hooks/use-engine";
import { useShell } from "@/stores/shell";

/**
 * The obsidian TopBar — an OPTIONAL expansion, collapsed by default. When
 * collapsed it renders NOTHING (not even a thin edge bar): the Dockview tab
 * strip inside `WorkspaceSurface` then sits at the very top of the page, with
 * no uncollapsible chrome consuming vertical space above it. It is reopened
 * from the LeftBar's "Show top bar" up-arrow (see `appshell/LeftBar.tsx`) —
 * the collapse control that used to live in a thin top bar moved to the left
 * taskbar so the top area is a true, fully-collapsible expansion.
 *
 * Expanded, it's ONE row: the atlas search COMBOBOX + export +
 * Upgrade/Account/Engine, with its own `ChevronUp` collapse button on the
 * right. The docview's own Dockview tab strip (inside `WorkspaceSurface`) is
 * the sole tab row now — the legacy `shell/TabStrip` that used to render
 * directly below this bar (and visually overlapped the dock's own strip) is
 * gone. `AccountChip` renders with `hideBilling` so its legacy inline
 * "Upgrade"/"Pro" control never duplicates the single `UpgradeButton` above.
 *
 * The search control is a real combobox now (`CommandPalette`): the bar IS the
 * input, and results drop in an anchored dropdown beneath it. It shares the
 * Export button's `h-9` inside one `items-center` row — no baseline drift.
 */
export function TopBar({
  onAddWebsite,
  onSmartExport,
  onManageTokens,
  onManageBackups,
  searchRef,
}: {
  onAddWebsite: () => void;
  onSmartExport: () => void;
  onManageTokens: () => void;
  onManageBackups: () => void;
  /** App holds this handle so ⌘K / the LeftBar "Search" can open the results. */
  searchRef?: Ref<AtlasSearchHandle>;
}) {
  const topOpen = useShell((s) => s.topOpen);
  const engine = useEngine();

  // Collapsed: render nothing at all — no thin bar near the top. The top area
  // is reopened from the LeftBar's up-arrow ("Show top bar").
  if (!topOpen) return null;

  return (
    <header
      id="gv-topbar"
      aria-label="Top bar"
      className="flex shrink-0 flex-col border-b border-border bg-background"
    >
      <div className="flex h-12 items-center gap-2 px-3">
        <CommandPalette
          controllerRef={searchRef}
          barClassName="h-9 flex-1 bg-input hover:text-foreground"
          onAddWebsite={onAddWebsite}
          onSmartExport={onSmartExport}
          onManageTokens={onManageTokens}
          onManageBackups={onManageBackups}
        />

        <Button type="button" variant="outline" size="sm" className="h-9 shrink-0" onClick={onSmartExport}>
          <PackageSearch className="h-3.5 w-3.5" /> Export
        </Button>

        <div className="mx-1 h-5 w-px shrink-0 bg-border" aria-hidden />

        <UpgradeButton className="h-9 shrink-0" />
        <EngineStatus engine={engine} variant="chip" />
        <AccountChip hideBilling />

        <Tip label="Collapse top bar" side="bottom">
          <button
            type="button"
            onClick={() => useShell.getState().toggleTopBar()}
            aria-label="Collapse top bar"
            className="grid h-7 w-7 shrink-0 place-items-center rounded text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <ChevronUp className="h-3.5 w-3.5" aria-hidden />
          </button>
        </Tip>
      </div>
    </header>
  );
}
